import i18n, { changeLanguage, use as registerI18nPlugin } from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import es from "./locales/es.json";

declare const process: {
  env: {
    NODE_ENV?: string;
  };
};

export const LANGUAGE_KEY = "pinpadel.language";
export const SUPPORTED_LANGUAGES = ["es", "en"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const normalizeLanguage = (
  value: string | null | undefined
): AppLanguage | null => {
  const code = value?.toLowerCase().split("-")[0];
  return code === "es" || code === "en" ? code : null;
};

const readStoredLanguage = (): string | null => {
  try {
    return localStorage.getItem(LANGUAGE_KEY);
  } catch {
    return null;
  }
};

const resolveLanguage = (): AppLanguage => {
  if (process.env.NODE_ENV === "test") return "es";

  return (
    normalizeLanguage(readStoredLanguage()) ??
    normalizeLanguage(navigator.language) ??
    "es"
  );
};

const language = resolveLanguage();

void registerI18nPlugin(initReactI18next).init({
  resources: {
    es: { translation: es },
    en: { translation: en },
  },
  lng: language,
  fallbackLng: "es",
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

if (typeof document !== "undefined") {
  document.documentElement.lang = language;
}

export const setAppLanguage = (nextLanguage: AppLanguage) => {
  try {
    localStorage.setItem(LANGUAGE_KEY, nextLanguage);
  } catch {
    // Preference stays in memory when storage is unavailable.
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = nextLanguage;
  }
  void changeLanguage(nextLanguage);
};

export const currentLanguage = (): AppLanguage =>
  normalizeLanguage(i18n.resolvedLanguage) ?? "es";

export const dateLocale = () => (currentLanguage() === "en" ? "en" : "es");

export default i18n;
